import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { RiAddLine, RiEditLine } from '@remixicon/react';

import { Button } from '@/design-system/button';
import { Field } from '@/design-system/field';
import { FormModal } from '@/design-system/form-modal';
import { Hint } from '@/design-system/hint';
import { IconButton } from '@/design-system/icon-button';
import { Input, InputRoot } from '@/design-system/input';
import { Label } from '@/design-system/label';
import { SearchSelect } from '@/design-system/search-select';
import {
  SegmentedControl,
  SegmentedItem,
} from '@/design-system/segmented-control';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/select';
import { Spinner } from '@/design-system/spinner';
import { Checkbox } from '@/design-system/checkbox';
import { toast } from '@/design-system/toaster';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import { useProvidersDropdown } from '@/pages/shipments/components/useProvidersDropdown';
import {
  stopWorkflowsAPI,
  UpsertWorkflowPayload,
  WORKFLOW_EXIM_LABELS,
  WORKFLOW_FIELD_TYPE_LABELS,
  WORKFLOW_STATUS_LABELS,
  WORKFLOW_STOP_INTENT_LABELS,
  WORKFLOW_STOP_TYPE_LABELS,
  WorkflowExim,
  WorkflowFieldConfig,
  WorkflowFieldType,
  WorkflowStatus,
  WorkflowStopIntent,
  WorkflowStopType,
} from '@/services/api/stopWorkflows';

import WorkflowPhonePreview from './WorkflowPhonePreview';

interface WorkflowBuilderModalProps {
  isOpen: boolean;
  /** Null = create mode. An id = edit mode (definition loaded on open). */
  workflowId: string | null;
  onClose: () => void;
  onSaved: () => void;
}

export type ImageMode = 'exact' | 'multiple';

export interface FieldDraft {
  uid: string;
  label: string;
  type: WorkflowFieldType;
  required: boolean;
  imageMode: ImageMode;
  imageCount: string;
  maxLength: string;
  min: string;
  max: string;
}

export interface MilestoneDraft {
  uid: string;
  name: string;
  fields: FieldDraft[];
}

/**
 * Firebase Remote Config flag (boolean, default OFF) that offers the Signature
 * field type here. It stays OFF until every 4W driver runs an app that knows
 * SIGNATURE: an older app shows a camera for it, the server rejects the JPEG
 * (415) and the driver is stuck mid-route. Open it only after the release
 * checklist (TRD R1 / T12 / DT10): BE deployed, `app_version_four_wheel` set to
 * the SIGNATURE release with `feature_force_update_four_wheel` on, partners
 * told about the webhook change, ops/legal approved the pad statement.
 */
export const SIGNATURE_FIELD_FLAG = 'feature_workflow_signature_field';

/** Types always offered; SIGNATURE joins them behind SIGNATURE_FIELD_FLAG. */
const BASE_FIELD_TYPES: WorkflowFieldType[] = ['IMAGE', 'TEXT', 'NUMBER'];

/**
 * The type options for one field's select. A field that already is SIGNATURE
 * (a workflow saved while the flag was on, or via the API) keeps its option
 * even with the flag off, so editing the workflow never silently re-types it.
 */
const fieldTypeOptions = (
  current: WorkflowFieldType,
  signatureEnabled: boolean
): WorkflowFieldType[] =>
  signatureEnabled || current === 'SIGNATURE'
    ? [...BASE_FIELD_TYPES, 'SIGNATURE']
    : BASE_FIELD_TYPES;

let uidCounter = 0;
const nextUid = () => `draft-${++uidCounter}`;

/**
 * Display-only mirror of the server-side key generator: keys are
 * SYSTEM-GENERATED from names — "Foto Depan Kendaraan" → "fotoDepanKendaraan".
 */
const deriveKey = (value: string): string => {
  const words = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return words
    .map((word, index) =>
      index === 0 ? word : word[0].toUpperCase() + word.slice(1)
    )
    .join('');
};

/** Per-type config inputs at their defaults — what a fresh type starts from. */
const BLANK_CONFIG: Pick<
  FieldDraft,
  'imageMode' | 'imageCount' | 'maxLength' | 'min' | 'max'
> = {
  imageMode: 'exact',
  imageCount: '1',
  maxLength: '',
  min: '',
  max: '',
};

/** Validation keys of the per-type config inputs, cleared with the config. */
const CONFIG_ERROR_KEYS = ['imageCount', 'maxLength', 'min', 'max'] as const;

const emptyField = (): FieldDraft => ({
  uid: nextUid(),
  label: '',
  type: 'IMAGE',
  required: true,
  ...BLANK_CONFIG,
});

/** The label the recipient-name nudge adds, and its copy (design 2G). */
const RECIPIENT_NAME_LABEL = 'Recipient name';
const RECIPIENT_NAME_NUDGE = `No text field for the recipient's name. Add a Text field "${RECIPIENT_NAME_LABEL}"?`;

/**
 * A milestone that asks for a signature but has no Text field records a
 * signature nobody can put a name to. Not an error — the save still goes
 * through — just a nudge to add one (design 2G).
 */
const needsRecipientName = (milestone: MilestoneDraft): boolean =>
  milestone.fields.some((f) => f.type === 'SIGNATURE') &&
  !milestone.fields.some((f) => f.type === 'TEXT');

/**
 * The payload config for one field draft. SIGNATURE has no config at all and
 * sends `{}` — the server rejects any key on it.
 */
const fieldConfig = (field: FieldDraft): WorkflowFieldConfig | undefined => {
  switch (field.type) {
    case 'IMAGE':
      return field.imageMode === 'multiple'
        ? { multiple: true }
        : { imageCount: Number(field.imageCount) };
    case 'TEXT':
      return field.maxLength.trim() !== ''
        ? { maxLength: Number(field.maxLength) }
        : undefined;
    case 'NUMBER': {
      const config: { min?: number; max?: number } = {};
      if (field.min.trim() !== '') config.min = Number(field.min);
      if (field.max.trim() !== '') config.max = Number(field.max);
      return Object.keys(config).length ? config : undefined;
    }
    case 'SIGNATURE':
      return {};
  }
};

const emptyMilestone = (): MilestoneDraft => ({
  uid: nextUid(),
  name: '',
  // Zero fields by default: a fieldless milestone is a confirmation-only
  // checkpoint (rider slides to confirm; only a timestamp is recorded).
  fields: [],
});

const WorkflowBuilderModal: React.FC<WorkflowBuilderModalProps> = ({
  isOpen,
  workflowId,
  onClose,
  onSaved,
}) => {
  const isEdit = workflowId !== null;

  const [clientID, setClientID] = useState<number | null>(null);
  // Scope: a GLOBAL workflow (isGlobal) has clientID null and is visible to
  // every client. A client-specific default still beats a global default.
  const [isGlobal, setIsGlobal] = useState(false);
  const [name, setName] = useState('');
  const [stopType, setStopType] = useState<WorkflowStopType>('DROP_OFF');
  const [intent, setIntent] = useState<WorkflowStopIntent>('GENERIC');
  const [exim, setExim] = useState<WorkflowExim>('GENERAL');
  const [status, setStatus] = useState<WorkflowStatus>('ACTIVE');
  const [isDefault, setIsDefault] = useState(true);
  const [milestones, setMilestones] = useState<MilestoneDraft[]>([
    emptyMilestone(),
  ]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  /** Label shown for the selected client when it isn't in the dropdown page. */
  const [clientFallbackLabel, setClientFallbackLabel] = useState('');

  const providers = useProvidersDropdown(isOpen);
  const signatureEnabled = useFeatureFlag(SIGNATURE_FIELD_FLAG, 'boolean');

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setIsSubmitting(false);
    if (!workflowId) {
      setClientID(null);
      setIsGlobal(false);
      setClientFallbackLabel('');
      setName('');
      setStopType('DROP_OFF');
      setIntent('GENERIC');
      setExim('GENERAL');
      setStatus('ACTIVE');
      // New workflows default to being the default: the common case is a client
      // with one workflow per slot, and an empty slot REQUIRES one.
      setIsDefault(true);
      setMilestones([emptyMilestone()]);
      return;
    }
    setLoading(true);
    stopWorkflowsAPI
      .get(workflowId)
      .then((detail) => {
        setClientID(detail.clientID);
        setIsGlobal(detail.clientID == null);
        // Prefer the frozen snapshot name; the dropdown page may not contain
        // this client, and the numeric id is the last resort. A global has no
        // client, so there is no fallback label to show.
        setClientFallbackLabel(
          detail.clientID == null
            ? ''
            : (detail.client?.name ?? `Client #${detail.clientID}`)
        );
        setName(detail.name);
        setStopType(detail.stopType);
        setIntent(detail.intent ?? 'GENERIC');
        setExim(detail.exim ?? 'GENERAL');
        setStatus(detail.status);
        setIsDefault(detail.isDefault);
        setMilestones(
          detail.milestones.map((milestone) => ({
            uid: nextUid(),
            name: milestone.name,
            fields: milestone.fields.map((field) => ({
              uid: nextUid(),
              label: field.label,
              type: field.type,
              required: field.required,
              imageMode: field.config?.multiple ? 'multiple' : 'exact',
              imageCount: String(field.config?.imageCount ?? 1),
              maxLength:
                field.config?.maxLength != null
                  ? String(field.config.maxLength)
                  : '',
              min: field.config?.min != null ? String(field.config.min) : '',
              max: field.config?.max != null ? String(field.config.max) : '',
            })),
          }))
        );
      })
      .catch((err) => {
        toast.error('Could not load workflow', {
          description: err instanceof Error ? err.message : 'Unknown error',
        });
        onClose();
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, workflowId]);

  const clearError = useCallback((key: string) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const patchMilestone = (uid: string, patch: Partial<MilestoneDraft>) => {
    setMilestones((prev) =>
      prev.map((m) => (m.uid === uid ? { ...m, ...patch } : m))
    );
  };

  const patchField = (
    milestoneUid: string,
    fieldUid: string,
    patch: Partial<FieldDraft>
  ) => {
    setMilestones((prev) =>
      prev.map((m) =>
        m.uid === milestoneUid
          ? {
              ...m,
              fields: m.fields.map((f) =>
                f.uid === fieldUid ? { ...f, ...patch } : f
              ),
            }
          : m
      )
    );
  };

  /**
   * Switch a field's type from a clean config: a stale IMAGE count or TEXT max
   * length never rides along into the new type's payload (design M7), and the
   * old type's validation errors go with it.
   */
  const changeFieldType = (
    milestoneUid: string,
    field: FieldDraft,
    type: WorkflowFieldType
  ) => {
    if (type === field.type) return;
    patchField(milestoneUid, field.uid, { type, ...BLANK_CONFIG });
    CONFIG_ERROR_KEYS.forEach((key) => clearError(`${field.uid}.${key}`));
  };

  /**
   * Add the "Recipient name" Text field the nudge suggests, just before the
   * milestone's first Signature — the order the rider app shows them in.
   */
  const addRecipientName = (milestone: MilestoneDraft) => {
    const at = milestone.fields.findIndex((f) => f.type === 'SIGNATURE');
    const nameField: FieldDraft = {
      ...emptyField(),
      label: RECIPIENT_NAME_LABEL,
      type: 'TEXT',
    };
    const fields = [...milestone.fields];
    fields.splice(at < 0 ? fields.length : at, 0, nameField);
    patchMilestone(milestone.uid, { fields });
  };

  const moveMilestone = (index: number, direction: -1 | 1) => {
    setMilestones((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {};
    // A global workflow has no client; only a client-scoped one requires one.
    if (!isGlobal && clientID == null)
      nextErrors.clientID = 'Client is required';
    if (!name.trim()) nextErrors.name = 'Name is required';
    if (milestones.length === 0) {
      nextErrors.milestones = 'A workflow requires at least 1 milestone';
    }

    const milestoneKeys = new Set<string>();
    for (const milestone of milestones) {
      const milestoneKey = deriveKey(milestone.name);
      if (!milestoneKey) {
        nextErrors[`${milestone.uid}.name`] = 'Milestone name is required';
      } else if (milestoneKeys.has(milestoneKey)) {
        nextErrors[`${milestone.uid}.name`] =
          'Another milestone generates the same key — use a distinct name';
      }
      milestoneKeys.add(milestoneKey);

      const fieldKeys = new Set<string>();
      for (const field of milestone.fields) {
        const fieldKey = deriveKey(field.label);
        if (!fieldKey) {
          nextErrors[`${field.uid}.label`] = 'Label is required';
        } else if (fieldKeys.has(fieldKey)) {
          nextErrors[`${field.uid}.label`] =
            'Another field generates the same key — use a distinct label';
        }
        fieldKeys.add(fieldKey);

        if (field.type === 'IMAGE' && field.imageMode === 'exact') {
          const count = Number(field.imageCount);
          if (!Number.isInteger(count) || count < 1) {
            nextErrors[`${field.uid}.imageCount`] = 'Count must be ≥ 1';
          }
        }
        if (field.type === 'TEXT' && field.maxLength.trim() !== '') {
          const maxLength = Number(field.maxLength);
          if (!Number.isInteger(maxLength) || maxLength < 1) {
            nextErrors[`${field.uid}.maxLength`] = 'Max length must be ≥ 1';
          }
        }
        if (field.type === 'NUMBER') {
          const min = field.min.trim() === '' ? null : Number(field.min);
          const max = field.max.trim() === '' ? null : Number(field.max);
          if (min !== null && !Number.isFinite(min)) {
            nextErrors[`${field.uid}.min`] = 'Min must be a number';
          }
          if (max !== null && !Number.isFinite(max)) {
            nextErrors[`${field.uid}.max`] = 'Max must be a number';
          }
          if (
            min !== null &&
            max !== null &&
            Number.isFinite(min) &&
            Number.isFinite(max) &&
            min > max
          ) {
            nextErrors[`${field.uid}.min`] = 'Min cannot exceed max';
          }
        }
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildPayload = (): UpsertWorkflowPayload => ({
    // null → a GLOBAL (client-agnostic) workflow.
    clientID: isGlobal ? null : clientID!,
    name: name.trim(),
    stopType,
    // Intent is a DROP_OFF concept; PICKUP is always GENERIC (omit).
    intent: stopType === 'DROP_OFF' ? intent : undefined,
    exim,
    isDefault,
    status,
    milestones: milestones.map((milestone, index) => ({
      name: milestone.name.trim(),
      sequence: index + 1,
      fields: milestone.fields.map((field) => ({
        label: field.label.trim(),
        type: field.type,
        required: field.required,
        config: fieldConfig(field),
      })),
    })),
  });

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error('Please fix the highlighted fields');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = buildPayload();
      if (isEdit && workflowId) {
        await stopWorkflowsAPI.update(workflowId, payload);
        toast.success('Workflow updated', { description: payload.name });
      } else {
        await stopWorkflowsAPI.create(payload);
        toast.success('Workflow created', { description: payload.name });
      }
      onSaved();
    } catch (err) {
      toast.error(
        isEdit ? 'Could not update workflow' : 'Could not create workflow',
        { description: err instanceof Error ? err.message : 'Unknown error' }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const clientOptions = useMemo(
    () =>
      providers.options.map((provider) => ({
        value: provider.id,
        label: provider.name,
      })),
    [providers.options]
  );

  const keyCls =
    'font-mono text-[11px] text-text-sub-600 tabular-nums tracking-tight';

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      position="right"
      size="full"
      confirmOnClose
      icon={isEdit ? <RiEditLine /> : <RiAddLine />}
      iconTone="primary"
      title={isEdit ? 'Edit workflow' : 'New workflow'}
      subtitle={
        isEdit
          ? 'Full replace — in-flight stops keep the definition they were created with.'
          : 'Define what a rider must capture at this client’s stops, milestone by milestone.'
      }
      footer={
        <>
          <Button tone="neutral" style="stroke" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || loading}
            loading={isSubmitting}
          >
            {isEdit ? 'Save changes' : 'Create workflow'}
          </Button>
        </>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_300px] lg:items-start lg:gap-8">
          <div className="space-y-5">
            {/* ── Workflow header ─────────────────────────────────────── */}
            <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
              <Field>
                <Label htmlFor="wf-client" required={!isGlobal}>
                  Client
                </Label>
                <SegmentedControl
                  value={isGlobal ? 'GLOBAL' : 'CLIENT'}
                  onValueChange={(v: string) => {
                    if (!v) return;
                    const global = v === 'GLOBAL';
                    setIsGlobal(global);
                    if (global) {
                      // A global has no client — clear any picked client.
                      setClientID(null);
                      setClientFallbackLabel('');
                    }
                    clearError('clientID');
                  }}
                  className="w-full [&>*]:flex-1"
                >
                  <SegmentedItem value="CLIENT">Client tertentu</SegmentedItem>
                  <SegmentedItem value="GLOBAL">Semua client</SegmentedItem>
                </SegmentedControl>
                {isGlobal ? (
                  <Hint>
                    Workflow global bisa dipakai semua client. Default milik
                    client tetap menang atas default global untuk slot yang
                    sama.
                  </Hint>
                ) : (
                  <>
                    <SearchSelect
                      options={clientOptions}
                      value={clientID ?? undefined}
                      selectedLabelFallback={
                        clientID != null
                          ? providers.labelFor(clientID) || clientFallbackLabel
                          : undefined
                      }
                      onChange={(value) => {
                        setClientID(Number(value));
                        clearError('clientID');
                      }}
                      placeholder="Select client…"
                      error={!!errors.clientID}
                      loading={providers.loading}
                      hasMore={providers.hasMore}
                      disableLocalFilter
                      onSearchChange={providers.setSearch}
                      onLoadMore={providers.loadMore}
                    />
                    {errors.clientID && (
                      <Hint tone="error">{errors.clientID}</Hint>
                    )}
                  </>
                )}
              </Field>

              <Field>
                <Label htmlFor="wf-name" required>
                  Name
                </Label>
                <InputRoot size="md" invalid={!!errors.name}>
                  <Input
                    id="wf-name"
                    value={name}
                    placeholder="e.g. FORE outlet delivery"
                    onChange={(e) => {
                      setName(e.target.value);
                      clearError('name');
                    }}
                  />
                </InputRoot>
                {errors.name && <Hint tone="error">{errors.name}</Hint>}
              </Field>

              <Field>
                <Label htmlFor="wf-stop-type" required>
                  Stop type
                </Label>
                <SegmentedControl
                  value={stopType}
                  onValueChange={(v: string) => {
                    if (!v) return;
                    const next = v as WorkflowStopType;
                    setStopType(next);
                    // Intent is a DROP_OFF concept; PICKUP is always GENERIC.
                    if (next === 'PICKUP') setIntent('GENERIC');
                  }}
                  className="w-full [&>*]:flex-1"
                >
                  {(['PICKUP', 'DROP_OFF'] as WorkflowStopType[]).map((opt) => (
                    <SegmentedItem key={opt} value={opt}>
                      {WORKFLOW_STOP_TYPE_LABELS[opt]}
                    </SegmentedItem>
                  ))}
                </SegmentedControl>
                <Hint>Gates only stops of this type — never both.</Hint>
              </Field>

              {stopType === 'DROP_OFF' && (
                <Field>
                  <Label htmlFor="wf-intent">Tujuan drop-off</Label>
                  <SegmentedControl
                    value={intent}
                    onValueChange={(v: string) => {
                      if (v) setIntent(v as WorkflowStopIntent);
                    }}
                    className="w-full [&>*]:flex-1"
                  >
                    {(
                      [
                        'GENERIC',
                        'UNLOAD',
                        'RETURN_CONTAINER',
                      ] as WorkflowStopIntent[]
                    ).map((opt) => (
                      <SegmentedItem key={opt} value={opt}>
                        {WORKFLOW_STOP_INTENT_LABELS[opt]}
                      </SegmentedItem>
                    ))}
                  </SegmentedControl>
                  <Hint>
                    Lets one client run separate drop-off workflows — e.g.
                    bongkar muatan vs balikin kontainer — each with its own
                    milestones.
                  </Hint>
                </Field>
              )}

              <Field>
                <Label htmlFor="wf-exim">Type</Label>
                <SegmentedControl
                  value={exim}
                  onValueChange={(v: string) => {
                    if (v) setExim(v as WorkflowExim);
                  }}
                  className="w-full [&>*]:flex-1"
                >
                  {(['GENERAL', 'EXPORT', 'IMPORT'] as WorkflowExim[]).map(
                    (opt) => (
                      <SegmentedItem key={opt} value={opt}>
                        {WORKFLOW_EXIM_LABELS[opt]}
                      </SegmentedItem>
                    )
                  )}
                </SegmentedControl>
                <Hint>
                  Shipment type this workflow serves — a client can hold a separate
                  Import and Export default for the same stop type &amp; intent.
                  Umum (GENERAL) is the fallback for untagged bookings.
                </Hint>
              </Field>

              <Field>
                <Label htmlFor="wf-status">Status</Label>
                <SegmentedControl
                  value={status}
                  onValueChange={(v: string) => {
                    if (v) setStatus(v as WorkflowStatus);
                  }}
                  className="w-full [&>*]:flex-1"
                >
                  {(['ACTIVE', 'INACTIVE'] as WorkflowStatus[]).map((opt) => (
                    <SegmentedItem key={opt} value={opt}>
                      {WORKFLOW_STATUS_LABELS[opt]}
                    </SegmentedItem>
                  ))}
                </SegmentedControl>
                <Hint>
                  A client may run several active workflows per stop type and
                  tujuan — only one of them is the default.
                </Hint>
              </Field>

              {status === 'ACTIVE' && (
                <Field>
                  <Label htmlFor="wf-default">Workflow default</Label>
                  <SegmentedControl
                    value={isDefault ? 'YES' : 'NO'}
                    onValueChange={(v: string) => {
                      if (v) setIsDefault(v === 'YES');
                    }}
                    className="w-full [&>*]:flex-1"
                  >
                    <SegmentedItem value="YES">Jadikan default</SegmentedItem>
                    <SegmentedItem value="NO">Bukan default</SegmentedItem>
                  </SegmentedControl>
                  <Hint>
                    {isDefault ? (
                      <>
                        Dipakai otomatis saat ops tidak memilih workflow di Buat
                        Shipment. Kalau slot ini sudah punya default, simpan
                        sebagai <strong>Bukan default</strong> lalu promosikan
                        dari daftar workflow.
                      </>
                    ) : (
                      <>
                        Tetap aktif dan bisa dipilih manual, tapi tidak dipakai
                        otomatis. Slot yang belum punya default sama sekali
                        wajib diisi <strong>Jadikan default</strong>.
                      </>
                    )}
                  </Hint>
                </Field>
              )}
            </div>

            {/* ── Milestones ──────────────────────────────────────────── */}
            <div className="space-y-2.5">
              <div className="flex items-center justify-between border-b border-stroke-soft-200 pb-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-text-sub-600">
                  Milestones · executed in order
                </p>
                <Button
                  tone="neutral"
                  style="stroke"
                  size="xs"
                  onClick={() =>
                    setMilestones((prev) => [...prev, emptyMilestone()])
                  }
                >
                  <i className="ri-add-line mr-1 text-[14px]" />
                  Add milestone
                </Button>
              </div>
              {errors.milestones && (
                <Hint tone="error">{errors.milestones}</Hint>
              )}

              {milestones.map((milestone, mIndex) => (
                <div
                  key={milestone.uid}
                  className="rounded-lg border border-stroke-soft-200"
                >
                  {/* Milestone header — single dense row */}
                  <div className="flex items-center gap-2 rounded-t-lg border-b border-stroke-soft-200 bg-bg-weak-50 px-2.5 py-1.5">
                    <span className="w-5 shrink-0 text-center font-mono text-[11px] font-semibold text-accent tabular-nums">
                      {String(mIndex + 1).padStart(2, '0')}
                    </span>
                    <div className="min-w-0 flex-1">
                      <InputRoot
                        size="sm"
                        invalid={!!errors[`${milestone.uid}.name`]}
                      >
                        <Input
                          value={milestone.name}
                          placeholder="Milestone name, e.g. Arrival evidence"
                          onChange={(e) => {
                            patchMilestone(milestone.uid, {
                              name: e.target.value,
                            });
                            clearError(`${milestone.uid}.name`);
                          }}
                        />
                      </InputRoot>
                    </div>
                    {milestone.name.trim() !== '' && (
                      <span
                        className={`hidden max-w-[140px] truncate sm:block ${keyCls}`}
                        title={`key: ${deriveKey(milestone.name)}`}
                      >
                        {deriveKey(milestone.name)}
                      </span>
                    )}
                    <div className="flex shrink-0 items-center">
                      <IconButton
                        tone="neutral"
                        style="ghost"
                        size="xs"
                        aria-label="Move milestone up"
                        disabled={mIndex === 0}
                        onClick={() => moveMilestone(mIndex, -1)}
                      >
                        <i className="ri-arrow-up-line" />
                      </IconButton>
                      <IconButton
                        tone="neutral"
                        style="ghost"
                        size="xs"
                        aria-label="Move milestone down"
                        disabled={mIndex === milestones.length - 1}
                        onClick={() => moveMilestone(mIndex, 1)}
                      >
                        <i className="ri-arrow-down-line" />
                      </IconButton>
                      <IconButton
                        tone="destructive"
                        style="ghost"
                        size="xs"
                        aria-label="Remove milestone"
                        onClick={() =>
                          setMilestones((prev) =>
                            prev.filter((m) => m.uid !== milestone.uid)
                          )
                        }
                      >
                        <i className="ri-delete-bin-line" />
                      </IconButton>
                    </div>
                  </div>
                  {errors[`${milestone.uid}.name`] && (
                    <div className="px-2.5 pt-1.5">
                      <Hint tone="error">
                        {errors[`${milestone.uid}.name`]}
                      </Hint>
                    </div>
                  )}

                  {/* Fields — dense rows separated by hairlines */}
                  <div className="px-2.5 py-1.5">
                    {milestone.fields.length === 0 && (
                      <p className="py-1 text-[11px] leading-relaxed text-text-sub-600">
                        <i className="ri-information-line mr-1 align-[-2px] text-[13px]" />
                        <span className="font-semibold text-text-strong-950">
                          Confirmation only
                        </span>{' '}
                        — rider slides to confirm; a timestamp is recorded. Add
                        fields to require photos, text, or numbers.
                      </p>
                    )}
                    {milestone.fields.length > 0 && (
                      <div className="divide-y divide-stroke-soft-200">
                        {milestone.fields.map((field) => (
                          <div key={field.uid} className="py-1.5">
                            <div className="flex flex-wrap items-center gap-2">
                              <div className="min-w-[160px] flex-1">
                                <InputRoot
                                  size="sm"
                                  invalid={!!errors[`${field.uid}.label`]}
                                >
                                  <Input
                                    value={field.label}
                                    placeholder="Field label, e.g. Outlet front photo"
                                    onChange={(e) => {
                                      patchField(milestone.uid, field.uid, {
                                        label: e.target.value,
                                      });
                                      clearError(`${field.uid}.label`);
                                    }}
                                  />
                                </InputRoot>
                              </div>
                              <Select
                                value={field.type}
                                onValueChange={(value) =>
                                  changeFieldType(
                                    milestone.uid,
                                    field,
                                    value as WorkflowFieldType
                                  )
                                }
                              >
                                <SelectTrigger
                                  size="sm"
                                  className="w-[104px] shrink-0"
                                  aria-label="Field type"
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {fieldTypeOptions(
                                    field.type,
                                    signatureEnabled
                                  ).map((type) => (
                                    <SelectItem key={type} value={type}>
                                      {WORKFLOW_FIELD_TYPE_LABELS[type]}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>

                              {/* Per-type config, inline. SIGNATURE has none. */}
                              {field.type === 'IMAGE' && (
                                <div className="flex items-center gap-1.5">
                                  <SegmentedControl
                                    size="sm"
                                    value={field.imageMode}
                                    onValueChange={(v: string) => {
                                      if (v)
                                        patchField(milestone.uid, field.uid, {
                                          imageMode: v as ImageMode,
                                        });
                                    }}
                                  >
                                    <SegmentedItem value="exact">
                                      Exact
                                    </SegmentedItem>
                                    <SegmentedItem value="multiple">
                                      Multi ≥ 1
                                    </SegmentedItem>
                                  </SegmentedControl>
                                  {field.imageMode === 'exact' && (
                                    <div className="w-14">
                                      <InputRoot
                                        size="sm"
                                        invalid={
                                          !!errors[`${field.uid}.imageCount`]
                                        }
                                      >
                                        <Input
                                          type="number"
                                          min={1}
                                          value={field.imageCount}
                                          aria-label="Exact photo count"
                                          onChange={(e) => {
                                            patchField(
                                              milestone.uid,
                                              field.uid,
                                              { imageCount: e.target.value }
                                            );
                                            clearError(
                                              `${field.uid}.imageCount`
                                            );
                                          }}
                                        />
                                      </InputRoot>
                                    </div>
                                  )}
                                </div>
                              )}

                              {field.type === 'TEXT' && (
                                <div className="flex items-center gap-1.5">
                                  <span className={keyCls}>max len</span>
                                  <div className="w-16">
                                    <InputRoot
                                      size="sm"
                                      invalid={
                                        !!errors[`${field.uid}.maxLength`]
                                      }
                                    >
                                      <Input
                                        type="number"
                                        min={1}
                                        value={field.maxLength}
                                        placeholder="—"
                                        aria-label="Max length"
                                        onChange={(e) => {
                                          patchField(milestone.uid, field.uid, {
                                            maxLength: e.target.value,
                                          });
                                          clearError(`${field.uid}.maxLength`);
                                        }}
                                      />
                                    </InputRoot>
                                  </div>
                                </div>
                              )}

                              {field.type === 'NUMBER' && (
                                <div className="flex items-center gap-1.5">
                                  <span className={keyCls}>min</span>
                                  <div className="w-14">
                                    <InputRoot
                                      size="sm"
                                      invalid={!!errors[`${field.uid}.min`]}
                                    >
                                      <Input
                                        type="number"
                                        value={field.min}
                                        placeholder="—"
                                        aria-label="Min"
                                        onChange={(e) => {
                                          patchField(milestone.uid, field.uid, {
                                            min: e.target.value,
                                          });
                                          clearError(`${field.uid}.min`);
                                        }}
                                      />
                                    </InputRoot>
                                  </div>
                                  <span className={keyCls}>max</span>
                                  <div className="w-14">
                                    <InputRoot
                                      size="sm"
                                      invalid={!!errors[`${field.uid}.max`]}
                                    >
                                      <Input
                                        type="number"
                                        value={field.max}
                                        placeholder="—"
                                        aria-label="Max"
                                        onChange={(e) => {
                                          patchField(milestone.uid, field.uid, {
                                            max: e.target.value,
                                          });
                                          clearError(`${field.uid}.max`);
                                        }}
                                      />
                                    </InputRoot>
                                  </div>
                                </div>
                              )}

                              <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-[11px] text-text-sub-600">
                                <Checkbox
                                  checked={field.required}
                                  onCheckedChange={(checked) =>
                                    patchField(milestone.uid, field.uid, {
                                      required: checked === true,
                                    })
                                  }
                                />
                                Required
                              </label>

                              <IconButton
                                tone="destructive"
                                style="ghost"
                                size="xs"
                                aria-label="Remove field"
                                onClick={() =>
                                  patchMilestone(milestone.uid, {
                                    fields: milestone.fields.filter(
                                      (f) => f.uid !== field.uid
                                    ),
                                  })
                                }
                              >
                                <i className="ri-delete-bin-line" />
                              </IconButton>
                            </div>

                            {field.label.trim() !== '' && (
                              <p className={`mt-0.5 ${keyCls}`}>
                                key: {deriveKey(field.label)}
                              </p>
                            )}
                            {errors[`${field.uid}.label`] && (
                              <Hint tone="error">
                                {errors[`${field.uid}.label`]}
                              </Hint>
                            )}
                            {errors[`${field.uid}.imageCount`] && (
                              <Hint tone="error">
                                {errors[`${field.uid}.imageCount`]}
                              </Hint>
                            )}
                            {errors[`${field.uid}.maxLength`] && (
                              <Hint tone="error">
                                {errors[`${field.uid}.maxLength`]}
                              </Hint>
                            )}
                            {(errors[`${field.uid}.min`] ||
                              errors[`${field.uid}.max`]) && (
                              <Hint tone="error">
                                {errors[`${field.uid}.min`] ||
                                  errors[`${field.uid}.max`]}
                              </Hint>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {needsRecipientName(milestone) && (
                      <div
                        data-testid={`recipient-name-nudge-${mIndex}`}
                        className="mt-1.5 flex items-center justify-between gap-2 rounded-gsm bg-(--theme-accent-alpha-10) px-2.5 py-1.5"
                      >
                        <p className="text-[11px] leading-relaxed text-text-strong-950">
                          <i className="ri-information-line mr-1 align-[-2px] text-[13px] text-accent" />
                          {RECIPIENT_NAME_NUDGE}
                        </p>
                        <Button
                          tone="primary"
                          style="ghost"
                          size="xs"
                          className="shrink-0"
                          onClick={() => addRecipientName(milestone)}
                        >
                          Add
                        </Button>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() =>
                        patchMilestone(milestone.uid, {
                          fields: [...milestone.fields, emptyField()],
                        })
                      }
                      className="mt-1 inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium text-text-sub-600 transition-colors hover:bg-bg-weak-50 hover:text-text-strong-950"
                    >
                      <i className="ri-add-line text-[13px]" />
                      Add field
                    </button>
                  </div>
                </div>
              ))}

              {/* Bottom add — reachable without scrolling back up */}
              <button
                type="button"
                onClick={() =>
                  setMilestones((prev) => [...prev, emptyMilestone()])
                }
                className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-stroke-soft-200 py-2 text-[12px] font-medium text-text-sub-600 transition-colors hover:border-stroke-sub-300 hover:bg-bg-weak-50 hover:text-text-strong-950"
              >
                <i className="ri-add-line text-[14px]" />
                Add milestone
              </button>
            </div>
          </div>

          {/* ── Rider m-app preview ─────────────────────────────────── */}
          <div className="sticky top-0 hidden lg:block">
            <WorkflowPhonePreview
              milestones={milestones}
              clientName={
                clientID != null
                  ? providers.labelFor(clientID) || clientFallbackLabel
                  : undefined
              }
              stopType={stopType}
            />
          </div>
        </div>
      )}
    </FormModal>
  );
};

export default WorkflowBuilderModal;
